import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Paper,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';

// Custom label for pie chart
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  index,
  payload
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="12"
      fontWeight="bold"
    >
      {payload.value}
    </text>
  );
};

// Tooltip component
const CustomTooltip = ({ active, payload, isMobile }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          maxWidth: 280,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: 2,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
          ml: isMobile ? 1 : 0
        }}
      >
        <Typography 
          variant={isMobile ? "subtitle1" : "h6"} 
          gutterBottom 
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700
          }}
        >
          {data.name}
        </Typography>
        <Typography variant={isMobile ? "body2" : "body1"} gutterBottom>
          <strong>Count:</strong> {data.value}
        </Typography>
        {data.percentage && (
          <Typography variant="caption" color="text.secondary">
            {data.percentage}% of total
          </Typography>
        )}
      </Paper>
    );
  }
  return null;
};

// Legend component
const CustomLegend = ({ payload, isMobile }) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      gap: isMobile ? 1 : 2, 
      mt: isMobile ? 2 : 3,
      flexWrap: 'wrap'
    }}>
      {payload.map((entry, index) => (
        <Chip
          key={index}
          label={`${entry.value}: ${entry.payload.value}`}
          size={isMobile ? "small" : "medium"}
          variant="outlined"
          sx={{
            borderColor: entry.color,
            color: entry.color,
            fontWeight: 600,
            fontSize: '0.875rem',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: entry.color,
              color: 'white',
              transform: 'scale(1.05)',
            }
          }}
        />
      ))}
    </Box>
  );
};

function PieChartShow({ adminUsers }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!adminUsers || adminUsers.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography variant="h6" color="text.secondary">
          No user data available to display chart.
        </Typography>
      </Box>
    );
  }

  const calculateTotals = (data) => {
    const totalLicenses = data.reduce(
      (sum, user) => sum + (user.TOTAL_LICENSES || 0),
      0
    );
    const totalRemainingLicenses = data.reduce(
      (sum, user) => sum + (user.REMAINING_LICENSES || 0),
      0
    );
    const totalTestsSubmitted = totalLicenses - totalRemainingLicenses;

    return {
      totalLicenses,
      totalRemainingLicenses,
      totalTestsSubmitted
    };
  };

  const {
    totalLicenses,
    totalRemainingLicenses,
    totalTestsSubmitted
  } = calculateTotals(adminUsers);

  const pieData = [
    {
      name: 'Tests Submitted',
      value: totalTestsSubmitted,
      color: '#10B981', // Modern green
      percentage: totalLicenses
        ? ((totalTestsSubmitted / totalLicenses) * 100).toFixed(1)
        : 0
    },
    {
      name: 'Remaining Licenses',
      value: totalRemainingLicenses,
      color: '#3B82F6', // Modern blue
      percentage: totalLicenses
        ? ((totalRemainingLicenses / totalLicenses) * 100).toFixed(1)
        : 0
    },
    {
      name: 'Total Licence',
      value: totalLicenses,
      color: '#8B5CF6', // Modern purple
    }
  ];

  return (
    <Box sx={{ width: '100%', height: "100%" }}>
      <Grid container spacing={isMobile ? 1 : 3}>
        <Grid item xs={12}>
          <Card 
            elevation={0}
            sx={{ 
              height: isMobile ? 'auto' : 400,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: 3,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
              }
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                component="h3"
                gutterBottom
                textAlign="center"
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  letterSpacing: '-0.5px'
                }}
              >
                License Usage Distribution
              </Typography>

              {isMobile ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'row', 
                  alignItems: 'center',
                  height: 250,
                  position: 'relative',
                  opacity: 1,
                  transition: 'opacity 0.3s ease-in-out'
                }}>
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%',
                    position: 'relative',
                    '& .recharts-pie': {
                      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                    }
                  }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          outerRadius="85%"  
                          innerRadius="30%"
                          fill="#8884d8"
                          dataKey="value"
                          stroke={theme.palette.background.paper}
                          strokeWidth={3}
                          paddingAngle={2}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ 
                  height: 300,
                  position: 'relative',
                  opacity: 1,
                  transition: 'opacity 0.3s ease-in-out',
                  '& .recharts-pie': {
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                  }
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius="85%"
                        innerRadius="30%"
                        fill="#8884d8"
                        dataKey="value"
                        stroke={theme.palette.background.paper}
                        strokeWidth={3}
                        paddingAngle={2}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip isMobile={isMobile} />} />
                      <Legend content={<CustomLegend isMobile={isMobile} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {isMobile && (
                <CustomLegend 
                  payload={pieData.map((entry, index) => ({
                    color: entry.color,
                    value: entry.name,
                    payload: entry
                  }))} 
                  isMobile={isMobile} 
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PieChartShow;